import _ from 'lodash';
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompanyPreview } from '../ifmsTypes';
import { JobNames } from '../framework.core/JobNames';

type DutyParentType = JobNames.OIHbl | JobNames.OIMbl |
  JobNames.OEMbl | JobNames.OEHbl |
  JobNames.AIMbl | JobNames.AIHbl |
  JobNames.AEMbl | JobNames.AEHbl |
  JobNames.OtherOP | JobNames.WHRcv | JobNames.WHShip;

type DutyEntity = 'bill' | 'invoice' | 'crdb';

interface ActiveDutyEntity {
  id: number,
  entity: DutyEntity,
  dutyAgent: CompanyPreview|null,
  dutyCustomer: CompanyPreview|null,
  hasDutyAgentInLineItems: boolean,
  hasDutyCustomerInLineItems: boolean,
};

interface DutyEntityHolder {
  id: number,
  entity: DutyEntity,
};

interface DutyTrackingStateElement {
  parentId: number,
  parent: DutyParentType,
  dutyAgentInParent: CompanyPreview|null,
  dutyCustomerInParent: CompanyPreview|null,
  dutyAgentHolders: Array<DutyEntityHolder>, // Currently saved in parent
  dutyCustomerHolders: Array<DutyEntityHolder>, // Currently saved in parent,
  activeScreens: Array<ActiveDutyEntity>,
};

interface DutyTrackingState {
  elements: Array<DutyTrackingStateElement>
};

const initialState:DutyTrackingState = {
  elements: [],
};

interface LoadDutyTrackingScreen {
  parentId: number,
  parent: DutyParentType,
  id: number,
  entity: DutyEntity,
  hasDutyAgentInLineItems: boolean,
  hasDutyCustomerInLineItems: boolean,
  dutyAgent: CompanyPreview|null, // Currently on screen in line items
  dutyCustomer: CompanyPreview|null, // Currently on screen in line items
  dutyAgentInParent: CompanyPreview|null, // Currently saved in parent
  dutyCustomerInParent: CompanyPreview|null, // Currently saved in parent
  dutyAgentHolders: Array<DutyEntityHolder>, // Currently saved in parent
  dutyCustomerHolders: Array<DutyEntityHolder>, // Currently saved in parent,
};

interface UnloadDutyTrackingScreen {
  parentId: number,
  parent: DutyParentType,
  id: number,
  entity: DutyEntity,
};

const activeDutyTrackingSlice = createSlice({
  name: 'activeDutyTracking',
  initialState,
  reducers: {
    loadScreen: (state, action: PayloadAction<LoadDutyTrackingScreen>) => {
      const newState = _.cloneDeep(state);
      const payload = action.payload;

      const parentIndex = newState.elements.findIndex(x => x.parent === action.payload.parent && x.parentId === action.payload.parentId);
      if (parentIndex < 0) {
        newState.elements.push({
          activeScreens: [{
            dutyAgent: payload.dutyAgent,
            dutyCustomer: payload.dutyCustomer,
            entity: payload.entity,
            id: payload.id,
            hasDutyAgentInLineItems: payload.hasDutyAgentInLineItems,
            hasDutyCustomerInLineItems: payload.hasDutyCustomerInLineItems,
          }],
          dutyAgentHolders: payload.dutyAgentHolders,
          dutyCustomerHolders: payload.dutyCustomerHolders,
          dutyAgentInParent: payload.dutyAgentInParent,
          dutyCustomerInParent: payload.dutyCustomerInParent,
          parent: payload.parent,
          parentId: payload.parentId
        });
      } else {
        newState.elements[parentIndex].dutyAgentHolders = payload.dutyAgentHolders;
        newState.elements[parentIndex].dutyCustomerHolders = payload.dutyCustomerHolders;
        newState.elements[parentIndex].dutyAgentInParent = payload.dutyAgentInParent;
        newState.elements[parentIndex].dutyCustomerInParent = payload.dutyCustomerInParent;

        const screenIndex = newState.elements[parentIndex].activeScreens.findIndex(s => s.id === payload.id && s.entity === payload.entity);
        if (screenIndex >= 0) {
          newState.elements[parentIndex].activeScreens[screenIndex].dutyAgent = payload.dutyAgent;
          newState.elements[parentIndex].activeScreens[screenIndex].dutyCustomer = payload.dutyCustomer;
          newState.elements[parentIndex].activeScreens[screenIndex].hasDutyAgentInLineItems = payload.hasDutyAgentInLineItems;
          newState.elements[parentIndex].activeScreens[screenIndex].hasDutyCustomerInLineItems = payload.hasDutyCustomerInLineItems;
        } else {
          newState.elements[parentIndex].activeScreens.push({
            dutyAgent: payload.dutyAgent,
            dutyCustomer: payload.dutyCustomer,
            entity: payload.entity,
            id: payload.id,
            hasDutyAgentInLineItems: payload.hasDutyAgentInLineItems,
            hasDutyCustomerInLineItems: payload.hasDutyCustomerInLineItems,
          });
        }
      }

      return newState;
    },
    unloadScreen: (state, action: PayloadAction<UnloadDutyTrackingScreen>) => {
      const newState = _.cloneDeep(state);
      const payload = action.payload;

      const parentIndex = newState.elements.findIndex(x => x.parent === action.payload.parent && x.parentId === action.payload.parentId);
      if (parentIndex < 0) {
        // Should never happen
        return newState;
      }

      const screenIndex = newState.elements[parentIndex].activeScreens.findIndex(s => s.id === payload.id && s.entity === payload.entity);
      if (screenIndex < 0) {
        // Should never happen
        return;
      }

      newState.elements[parentIndex].activeScreens.splice(screenIndex, 1);

      if (newState.elements[parentIndex].activeScreens.length <= 0) {
        newState.elements.splice(parentIndex, 1);
      }

      return newState;
    }
  }
});

export default activeDutyTrackingSlice.reducer;

export const { loadScreen, unloadScreen } = activeDutyTrackingSlice.actions;
